/**
 * Copyright (C) 2021 Tencent.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import imgSizeHandler from '@/utils/imgSizeHandler';
import imgToolHandler from '@/utils/imgToolHandler';
import TableHandler from '@/utils/tableContentHandler';
import FootnoteHoverHandler from '@/utils/footnoteHoverHandler';
import CodeHandler from '@/utils/codeBlockContentHandler';
import { drawioDialog } from '@/utils/dialog';
import { imgDrawioReg, getValueWithoutCode } from '@/utils/regexp';
import debounce from 'lodash/debounce';
import FormulaHandler from '@/utils/formulaUtilsHandler';
import ListHandler from '@/utils/listContentHandler';

/**
 * 预览区域的响应式工具栏
 */
export default class PreviewerBubble {
  /**
   *
   * @param {import('../Previewer').default} previewer
   */
  constructor(previewer) {
    /**
     * @property
     * @type {import('../Previewer').default}
     */
    this.previewer = previewer;
    /**
     * @property
     * @type {import('../Editor').default}
     */
    this.editor = previewer.editor;
    this.previewerDom = this.previewer.getDom();
    this.$cherry = previewer.$cherry;
    /**
     * @property
     * @type {{ [key: string]: HTMLDivElement}}
     */
    this.bubble = {};
    /**
     * @property
     * @type {{ [key: string]: { emit: (...args: any[]) => any, [key:string]: any }}}
     */
    this.bubbleHandler = {};
    this.init();
  }

  init() {
    // 记录cherry外层容器的overflow属性，在后续的操作中会临时修改overflow属性，所以需要先记录
    this.oldWrapperDomOverflow = this.previewer.$cherry.wrapperDom.style.overflow;
    this.previewerDom.addEventListener('click', this.$onClick.bind(this));
    this.previewerDom.addEventListener('mouseover', this.$onMouseOver.bind(this));
    // this.previewerDom.addEventListener('mouseout', this.$onMouseOut.bind(this));

    document.addEventListener('mousedown', (event) => {
      Object.values(this.bubbleHandler).forEach((handler) => handler.emit('mousedown', event));
    });
    document.addEventListener('mouseup', (event) => {
      Object.values(this.bubbleHandler).forEach((handler) =>
        handler.emit('mouseup', event, () => this.$removeAllPreviewerBubbles('click')),
      );
    });
    document.addEventListener('mousemove', (event) => {
      Object.values(this.bubbleHandler).forEach((handler) => handler.emit('mousemove', event));
    });
    document.addEventListener('keyup', (event) => {
      Object.values(this.bubbleHandler).forEach((handler) => handler.emit('keyup', event));
    });

    this.$cherry.$event.on('editor.size.change', () => {
      Object.values(this.bubbleHandler).forEach((handler) => handler.emit('resize', {}));
    });

    this.previewerDom.addEventListener(
      'scroll',
      (event) => {
        Object.values(this.bubbleHandler).forEach((handler) => handler.emit('scroll', event));
      },
      true,
    );
    this.$cherry.$event.on('previewerClose', () => this.$removeAllPreviewerBubbles());
    this.previewer.options.afterUpdateCallBack.push(() => {
      // 检查表格处理器是否需要重新创建
      this.$checkAndRecreateTableHandlers();

      Object.values(this.bubbleHandler).forEach((handler) =>
        handler.emit('previewUpdate', () => this.$removeAllPreviewerBubbles()),
      );
    });
    this.previewerDom.addEventListener('change', this.$onChange.bind(this));
    this.removeHoverBubble = debounce(() => this.$removeAllPreviewerBubbles('hover'), 400);
  }

  /**
   * 判断是否为代码块
   * @param {HTMLElement} element
   * @returns {boolean|Element}
   */
  isCherryCodeBlock(element) {
    if (!Element.prototype.closest) {
      Element.prototype.closest = function (selector) {
        let el = this;
        while (el) {
          if (el.matches(selector)) {
            return el;
          }
          el = el.parentElement;
        }
        return null;
      };
    }
    // 引用里的代码块先不支持所见即所得编辑
    if (this.$getClosestNode(element, 'BLOCKQUOTE') !== false) {
      return false;
    }
    const container = element.closest('div[data-type="codeBlock"]');
    if (!container) {
      return false;
    }
    return container;
  }

  /**
   * 是否为由cherry生成的表格，且不是简单表格
   * 现在也支持 HTML 表格语法
   * @param {HTMLElement} element
   * @returns {boolean|HTMLElement}
   */
  isCherryTable(element) {
    // 获取最近的表格元素
    const table = this.$getClosestNode(element, 'TABLE');
    if (!table) {
      return false;
    }

    // 排除简单表格
    const container = this.$getClosestNode(element, 'DIV');
    if (container && /simple-table/.test(container.className)) {
      return false;
    }

    return table;
  }

  /**
   * 是否开启了预览区操作 && 是否有编辑区
   * @returns {boolean}
   */
  $isEnableBubbleAndEditorShow() {
    if (!this.previewer.options.enablePreviewerBubble) {
      return false;
    }
    const cherryStatus = this.previewer.$cherry.getStatus();
    if (cherryStatus.editor === 'hide') {
      return false;
    }
    return true;
  }

  $onMouseOver(e) {
    /** @type {Event} */
    const { target } = e;
    // 这里要用Element，而不是HTMLElement
    if (!(target instanceof Element) || typeof target.tagName === 'undefined') {
      return;
    }
    switch (target.tagName) {
      case 'TD':
      case 'TH':
        if (!this.$isEnableBubbleAndEditorShow()) {
          return;
        }
        // eslint-disable-next-line no-case-declarations
        const table = this.isCherryTable(e.target);
        if (false === table) {
          return;
        }
        this.removeHoverBubble.cancel();
        this.$removeAllPreviewerBubbles('hover');
        // @ts-ignore
        this.$showTablePreviewerBubbles('hover', e.target, table);
        return;
      case 'PRE':
      case 'CODE':
      case 'SPAN':
      case 'DIV':
        // eslint-disable-next-line no-case-declarations
        const codeBlock = this.isCherryCodeBlock(e.target);
        if (codeBlock === false) {
          return;
        }
        this.showCodeBlockPreviewerBubbles('hover', codeBlock);
        return;
      case 'A':
        // @ts-ignore
        // eslint-disable-next-line no-case-declarations
        const bubbleCard = this.previewer?.$cherry?.options?.engine?.syntax?.footnote?.bubbleCard || false;
        if (bubbleCard !== false && /cherry-show-bubble-card/.test(e.target.className)) {
          this.removeHoverBubble.cancel();
          this.$removeAllPreviewerBubbles('hover');
          this.$showFootNoteBubbleCardPreviewerBubbles('hover', e.target, bubbleCard);
          return;
        }
    }
    this.removeHoverBubble();
  }

  $onMouseOut() {
    if (!this.previewer.options.enablePreviewerBubble) {
      return;
    }
    const cherryStatus = this.previewer.$cherry.getStatus();
    // 左侧编辑器被隐藏时不再提供后续功能
    if (cherryStatus.editor === 'hide') {
      return;
    }
    // this.removeHoverBubble();
  }

  $dealCheckboxClick(e) {
    const { target } = e;
    // 先计算是previewer中第几个checkbox
    const list = Array.from(this.previewerDom.querySelectorAll('.ch-icon-square, .ch-icon-check'));
    this.checkboxIdx = list.indexOf(target);

    // 然后找到Editor中对应的`- []`或者`- [ ]`进行修改
    const contents = getValueWithoutCode(this.editor.editor.getValue()).split('\n');

    let editorCheckboxCount = 0;
    // [ ]中的空格，或者[x]中的x的位置
    let targetLine = -1;
    let targetCh = -1;
    contents.forEach((lineContent, lineIdx) => {
      const tmp = lineContent.trim(); // 去掉句首的空格和制表符
      if (tmp.startsWith('- [ ]') || tmp.startsWith('- [x]')) {
        // 如果是个checkbox
        if (editorCheckboxCount === this.checkboxIdx) {
          targetLine = lineIdx;
          targetCh = lineContent.indexOf('- [') + 3;
        }
        editorCheckboxCount += 1;
      }
    });
    if (targetLine === -1) {
      // 无法找到对应的checkbox
      return;
    }
    this.editor.editor.setSelection({ line: targetLine, ch: targetCh }, { line: targetLine, ch: targetCh + 1 });
    this.editor.editor.replaceSelection(this.editor.editor.getSelection() === ' ' ? 'x' : ' ', 'around');
  }

  /**
   * 点击预览区域的事件处理
   * @param {MouseEvent} e
   * @returns
   */
  $onClick(e) {
    if (this.previewer.$cherry.options.callback.onClickPreview) {
      // 如果有自定义的onClickPreview回调函数，则先执行
      const ret = this.previewer.$cherry.options.callback.onClickPreview(e);
      // @ts-ignore
      if (ret === false) {
        return ret;
      }
    }
    const { target } = e;
    if (!(target instanceof Element)) {
      return;
    }

    // 编辑draw.io不受previewer.options.enablePreviewerBubble配置的影响
    if (target instanceof HTMLImageElement) {
      if (
        target.tagName === 'IMG' &&
        target.getAttribute('data-type') === 'drawio' &&
        this.$cherry.status.editor === 'show'
      ) {
        if (!this.beginChangeDrawioImg(target)) {
          return;
        }
        const xmlData = decodeURI(target.getAttribute('data-xml'));
        drawioDialog(
          this.previewer.$cherry.options.drawioIframeUrl,
          this.previewer.$cherry.options.drawioIframeStyle,
          xmlData,
          (newData) => {
            const { xmlData, base64 } = newData;
            this.editor.editor.replaceSelection(
              `(${base64}){data-type=drawio data-xml=${encodeURI(xmlData)}}`,
              'around',
            );
          },
        );
        return;
      }
    }

    // 点击展开代码块操作
    if (target.className === 'expand-btn ' || target.className === 'ch-icon ch-icon-expand') {
      const expandBtnDom = this.$getClosestNode(target, 'DIV');
      expandBtnDom.parentNode.parentNode.classList.remove('cherry-code-unExpand');
      expandBtnDom.parentNode.parentNode.classList.add('cherry-code-expand');
      if (this.$cherry.options.callback.onExpandCode) {
        const codeContent = expandBtnDom.parentNode.parentNode.innerText;
        this.$cherry.options.callback.onUnExpandCode(e, codeContent);
      }
      if (this.bubbleHandler?.hover?.unExpandDom) {
        this.bubbleHandler.hover.unExpandDom.classList.remove('hidden');
      }
    }

    // 不论enablePreviewerBubble是否开启，以下功能全都可以执行
    switch (target.tagName) {
      case 'A':
        // 如果配置了点击toc目录不更新location hash
        // @ts-ignore
        if (this.previewer.$cherry.options.toolbars.toc?.updateLocationHash === false) {
          if (target instanceof Element && target.nodeName === 'A' && /level-\d+/.test(target.className)) {
            const liNode = target.parentElement;
            const index = Array.from(liNode.parentElement.children).indexOf(liNode) - 1;
            this.previewer.scrollToHeadByIndex(index);
            e.stopPropagation();
            e.preventDefault();
          }
        }
        // 如果点击的是脚注
        if (target instanceof Element && target.nodeName === 'A' && /(footnote|footnote-ref)/.test(target.className)) {
          // @ts-ignore
          if (this.previewer.$cherry.options.engine?.syntax?.footnote?.refNumber?.clickRefNumberCallback) {
            const refNum = target.getAttribute('data-index');
            const refTitle = target.getAttribute('data-key');
            const content =
              this.previewer.getDomContainer().querySelector(`.one-footnote[data-index="${refNum}"]`).innerHTML ?? '';
            // 如果有自定义的clickRefNumberCallback回调函数，则先执行
            // @ts-ignore
            const ret = this.previewer.$cherry.options.engine.syntax.footnote.refNumber.clickRefNumberCallback(
              e,
              refNum,
              refTitle,
              content,
            );
            // @ts-ignore
            if (ret === false) {
              e.stopPropagation();
              e.preventDefault();
              return ret;
            }
          }
          /** 增加个潜规则逻辑，脚注跳转时是否更新location hash也跟随options.toolbars.toc.updateLocationHash 的配置 */
          // @ts-ignore
          if (this.previewer.$cherry.options.toolbars.toc?.updateLocationHash === false) {
            const id = target.getAttribute('href');
            this.previewer.scrollToId(id);
            e.stopPropagation();
            e.preventDefault();
          }
        }
        break;
    }

    if (!this.previewer.options.enablePreviewerBubble) {
      return;
    }
    // 只有双栏编辑模式才出现下面的功能
    // checkbox所见即所得编辑操作
    if (target.className === 'ch-icon ch-icon-square' || target.className === 'ch-icon ch-icon-check') {
      this.$dealCheckboxClick(e);
    }
    this.$removeAllPreviewerBubbles('click');
    if (typeof target.tagName === 'undefined') {
      return;
    }

    switch (target.tagName) {
      case 'IMG':
        if (target instanceof HTMLImageElement) {
          this.$showImgPreviewerBubbles(target, e);
        }
        break;
      case 'TD':
      case 'TH':
        if (target instanceof HTMLElement) {
          const table = this.isCherryTable(target);
          if (false === table) {
            return;
          }
          // @ts-ignore
          this.$showTablePreviewerBubbles('click', target, table);
        }
        break;
      case 'svg':
        if (target?.parentElement?.tagName === 'MJX-CONTAINER') {
          this.$showFormulaPreviewerBubbles('click', target, { x: e.pageX, y: e.pageY });
        }
        break;
      case 'A':
        e.stopPropagation(); // 阻止冒泡，避免触发预览区域的点击事件
        break;
      case 'P':
        if (
          target instanceof HTMLParagraphElement &&
          target.parentElement instanceof HTMLLIElement &&
          // 引用里的列表先不支持所见即所得编辑
          this.$getClosestNode(target, 'BLOCKQUOTE') === false
        ) {
          if (target.children.length !== 0) {
            // 富文本
            e.preventDefault();
            e.stopPropagation();
          }
          // 鼠标点击在列表的某项上时，为target增加contenteditable属性，使其可编辑
          target.setAttribute('contenteditable', 'true');
          target.focus();
          this.$showListPreviewerBubbles('click', target);
        }
        break;
    }
  }

  $onChange(e) {
    return;
  }

  $getClosestNode(node, targetNodeName) {
    if (!node || !node.tagName) {
      return false;
    }
    if (node.tagName === targetNodeName) {
      return node;
    }
    if (node.parentNode.tagName === 'BODY') {
      return false;
    }
    return this.$getClosestNode(node.parentNode, targetNodeName);
  }

  /**
   * 隐藏预览区域已经激活的工具栏
   * @param {string} trigger 移除指定的触发方式，不传默认全部移除
   */
  $removeAllPreviewerBubbles(trigger = '') {
    Object.entries(this.bubble)
      .filter(([key]) => !trigger || trigger === key)
      .forEach(([key, value]) => {
        value.remove();
        delete this.bubble[key];
      });
    Object.entries(this.bubbleHandler)
      .filter(([key]) => !trigger || trigger === key)
      .forEach(([key, value]) => {
        value.emit('remove');
        delete this.bubbleHandler[key];
      });
    if (Object.keys(this.bubbleHandler).length <= 0) {
      this.previewer.$cherry.wrapperDom.style.overflow = this.oldWrapperDomOverflow || '';
    }
  }

  /**
   * 检查并重新创建表格处理器
   * 当表格结构发生变化时，需要重新创建处理器以避免位置异常
   */
  $checkAndRecreateTableHandlers() {
    // 检查所有表格处理器
    Object.entries(this.bubbleHandler).forEach(([trigger, handler]) => {
      if (handler instanceof TableHandler) {
        // 检查表格是否仍然存在且有效
        if (!this.$isTableHandlerValid(handler)) {
          // 表格已不存在或无效，移除处理器
          this.$removePreviewerBubble(trigger);
        }
      }
    });
  }

  /**
   * 检查表格处理器是否仍然有效
   * @param {TableHandler} handler 表格处理器实例
   * @returns {boolean} 是否有效
   */
  $isTableHandlerValid(handler) {
    // 检查目标元素是否仍然存在
    if (!handler.target || !document.contains(handler.target)) {
      return false;
    }

    // 检查表格节点是否仍然存在
    const tableNode = handler.$getClosestNode(handler.target, 'TABLE');
    if (tableNode === false) {
      return false;
    }

    // 检查表格是否仍然在预览区域中
    if (!this.previewerDom.contains(tableNode)) {
      return false;
    }

    // 检查表格是否有有效的内容
    if (!tableNode.textContent || tableNode.textContent.trim() === '') {
      return false;
    }

    return true;
  }

  /**
   * 移除指定的预览气泡
   * @param {string} trigger 触发方式
   */
  $removePreviewerBubble(trigger) {
    if (this.bubble[trigger]) {
      this.bubble[trigger].remove();
      delete this.bubble[trigger];
    }
    if (this.bubbleHandler[trigger]) {
      this.bubbleHandler[trigger].emit('remove');
      delete this.bubbleHandler[trigger];
    }
    if (Object.keys(this.bubbleHandler).length <= 0) {
      this.previewer.$cherry.wrapperDom.style.overflow = this.oldWrapperDomOverflow || '';
    }
  }

  /**
   * hover到脚注的数字角标时展示悬浮卡片
   * @param {string} trigger 触发方式
   * @param {HTMLElement} htmlElement 触发的角标
   * @param {object} bubbleConfig 悬浮卡片的配置
   */
  $showFootNoteBubbleCardPreviewerBubbles(trigger, htmlElement, bubbleConfig) {
    if (this.bubbleHandler[trigger]) {
      if (this.bubbleHandler[trigger]?.aElement === htmlElement) {
        // 已经存在相同的target，直接返回
        this.bubbleHandler[trigger].showBubble();
        return;
      }
    }
    this.$createPreviewerBubbles(
      trigger,
      `footnote-ref-hover-handler cherry-markdown ${bubbleConfig.appendClass ?? ''}`,
    );
    const handler = new FootnoteHoverHandler(
      trigger,
      htmlElement,
      this.bubble[trigger],
      this.previewer.$cherry,
      bubbleConfig,
    );
    handler.showBubble();
    this.bubbleHandler[trigger] = handler;
  }

  /**
   * 为触发的table增加操作工具栏
   * @param {string} trigger 触发方式
   * @param {HTMLElement} htmlElement 用户触发的table dom
   */
  $showTablePreviewerBubbles(trigger, htmlElement, tableElement) {
    if (this.bubbleHandler[trigger]) {
      if (this.bubbleHandler[trigger].tableElement === tableElement) {
        // 已经存在相同的target，直接返回
        this.bubbleHandler[trigger].showBubble();
        return;
      }
    }
    this.$createPreviewerBubbles(trigger, trigger === 'click' ? 'table-content-handler' : 'table-hover-handler');
    const handler = new TableHandler(
      trigger,
      htmlElement,
      this.bubble[trigger],
      this.previewerDom,
      this.editor.editor,
      tableElement,
      this.previewer.$cherry,
    );
    handler.showBubble();
    this.bubbleHandler[trigger] = handler;
  }

  showCodeBlockPreviewerBubbles(trigger, htmlElement) {
    if (this.bubbleHandler[trigger]) {
      if (this.bubbleHandler[trigger].target === htmlElement) {
        // 已经存在相同的target，直接返回
        this.removeHoverBubble.cancel();
        return;
      }
    }
    this.$removeAllPreviewerBubbles('hover');
    this.$createPreviewerBubbles(trigger, `codeBlock-${trigger}-handler`);
    const handler = new CodeHandler(
      trigger,
      htmlElement,
      this.bubble[trigger],
      this.previewerDom,
      this.editor.editor,
      this,
    );
    handler.showBubble(this.$isEnableBubbleAndEditorShow());
    this.bubbleHandler[trigger] = handler;
  }

  /**
   * 为选中的图片增加操作工具栏
   * @param {HTMLImageElement} htmlElement 用户点击的图片dom
   */
  $showImgPreviewerBubbles(htmlElement, event) {
    this.$createPreviewerBubbles('click', 'img-handler');
    const list = Array.from(this.previewerDom.querySelectorAll('img'));
    this.totalImgs = list.length;
    this.imgIndex = list.indexOf(htmlElement);
    if (!this.beginChangeImgValue(htmlElement)) {
      return { emit: () => {} };
    }

    const imgSizeDiv = document.createElement('div');
    imgSizeDiv.className = 'cherry-previewer-img-size-handler';
    this.bubble.click.appendChild(imgSizeDiv);
    imgSizeHandler.showBubble(htmlElement, imgSizeDiv, this.previewerDom);
    imgSizeHandler.bindChange(this.changeImgSize.bind(this));

    const imgToolDiv = document.createElement('div');
    imgToolDiv.className = 'cherry-previewer-img-tool-handler';
    this.bubble.click.appendChild(imgToolDiv);
    imgToolHandler.showBubble(htmlElement, imgToolDiv, this.previewerDom, event, this.previewer.$cherry.getLocales());
    imgToolHandler.bindChange(this.changeImgStyle.bind(this));

    // 订阅编辑器大小变化事件
    const updateHandler = imgSizeHandler.updatePosition.bind(imgSizeHandler);
    this.$cherry.$event.on('editor.size.change', updateHandler);
    // 保存原始的remove方法
    const originalRemove = imgSizeHandler.remove;
    imgSizeHandler.remove = () => {
      this.$cherry.$event.off('editor.size.change', updateHandler);
      // 调用原始的remove方法
      return originalRemove.call(imgSizeHandler);
    };
    this.bubbleHandler.click = imgSizeHandler;
    this.bubbleHandler.imgTool = imgToolHandler;
  }

  /**
   * 为触发的公式增加操作工具栏
   * @param {string} trigger 触发方式
   * @param {Element} target 用户触发的公式dom
   * @param {{x?: number, y?: number}} options 额外参数
   */
  $showFormulaPreviewerBubbles(trigger, target, options = {}) {
    this.$createPreviewerBubbles(trigger, 'formula-hover-handler');
    const formulaHandler = new FormulaHandler(trigger, target, this.bubble[trigger], this.previewerDom, this.editor);
    formulaHandler.showBubble(options?.x || 0, options?.y || 0);
    this.bubbleHandler[trigger] = formulaHandler;
  }

  /**
   * 为触发的列表增加操作工具栏
   * @param {string} trigger 触发方式
   * @param {HTMLParagraphElement} target 用户触发的列表dom
   */
  $showListPreviewerBubbles(trigger, target, options = {}) {
    this.$createPreviewerBubbles(trigger, 'list-hover-handler');
    const listHandler = new ListHandler(trigger, target, this.bubble[trigger], this.previewerDom, this.editor);
    this.bubbleHandler[trigger] = listHandler;
  }

  /**
   * TODO: beginChangeDrawioImg 和 beginChangeImgValue 代码高度重合，后面有时间重构下，抽成一个可以复用的，可以避开代码块、行内代码影响的通用方法
   * 修改draw.io图片时选中编辑区域的对应文本
   * @param {*} htmlElement 图片node
   */
  beginChangeDrawioImg(htmlElement) {
    const allDrawioImgs = Array.from(this.previewerDom.querySelectorAll('img[data-type="drawio"]'));
    const totalDrawioImgs = allDrawioImgs.length;
    const drawioImgIndex = allDrawioImgs.indexOf(htmlElement);
    const content = getValueWithoutCode(this.editor.editor.getValue());
    const drawioImgsCode = content.match(imgDrawioReg);
    const testSrc = drawioImgsCode[drawioImgIndex]
      ? drawioImgsCode[drawioImgIndex].replace(/^!\[.*?\]\((.*?)\)/, '$1').trim()
      : '';
    if (drawioImgsCode.length === totalDrawioImgs || htmlElement.getAttribute('src') === testSrc) {
      // 如果drawio语法数量和预览区域的一样多
      const totalValue = content.split(imgDrawioReg);
      let line = 0;
      let beginCh = 0;
      let endCh = 0;
      let testIndex = 0;
      for (let i = 0; i < totalValue.length; i++) {
        const targetString = totalValue[i];
        if (targetString === drawioImgsCode[testIndex]) {
          // 如果找到目标代码
          if (testIndex === drawioImgIndex) {
            endCh = beginCh + targetString.length;
            beginCh += targetString.replace(/^(!\[[^\]]*])[^\n]*$/, '$1').length;
            this.editor.editor.setSelection({ line, ch: beginCh }, { line, ch: endCh });
            // 更新后需要再调用一次markText机制
            this.editor.dealSpecialWords();
            return true;
          }
          testIndex += 1;
        } else {
          line += targetString.match(/\n/g)?.length ?? 0;
          if (/\n/.test(targetString)) {
            // 如果有换行，则开始位置的字符计数从最后一个换行开始计数
            beginCh = targetString.replace(/^[\w\W]*\n([^\n]*)$/, '$1').length;
          } else {
            // 如果没有换行，则继续按上次的beginCh为起始开始计数
            beginCh += targetString.length;
          }
        }
      }
    }
    return false;
  }

  /**
   * 选中图片对应的MD语法
   * @param {*} htmlElement 图片node
   * @returns {boolean}
   */
  beginChangeImgValue(htmlElement) {
    const content = getValueWithoutCode(this.editor.editor.getValue());
    const src = htmlElement.getAttribute('src');
    const imgReg = /(!\[[^\n]*?\]\([^)]+\))/g;
    const contentImgs = content.match(imgReg);
    const testSrc = contentImgs[this.imgIndex]
      ? contentImgs[this.imgIndex].replace(/^!\[.*?\]\((.*?)\)/, '$1').trim()
      : '';
    if (contentImgs.length === this.totalImgs || src === testSrc) {
      // 如果图片语法数量和预览区域的一样多
      // 暂时不需要考虑手动输入img标签的场景 和 引用图片的场景
      const totalValue = content.split(imgReg);
      const imgDecoReg = /(#border|#B|#shadow|#S|#radius|#R)/g;
      const imgAlignReg = /(#center|#right|#left|#float-right|#float-left)/g;
      const imgSizeReg = /^!\[.*?((?:(?:#[0-9]+(px|em|pt|pc|in|mm|cm|ex|%)|auto) *)+).*?\].*$/;
      let line = 0;
      let beginCh = 0;
      let endCh = 0;
      let testIndex = 0;
      for (let i = 0; i < totalValue.length; i++) {
        const targetString = totalValue[i];
        if (targetString === contentImgs[testIndex]) {
          // 如果找到目标代码
          if (testIndex === this.imgIndex) {
            this.imgDeco = imgDecoReg.test(targetString) ? targetString.match(imgDecoReg).join(' ') : '';
            this.imgAlign = imgAlignReg.test(targetString) ? targetString.match(imgAlignReg).join(' ') : '';
            this.imgSize = imgSizeReg.test(targetString) ? targetString.replace(imgSizeReg, '$1') : '';
            beginCh += targetString.replace(/^(!\[[^#\]]*).*$/, '$1').length;
            endCh = beginCh + targetString.replace(/^(!\[[^#\]]*)([^\]]*?)\].*$/, '$2').length;
            this.editor.editor.setSelection({ line, ch: beginCh }, { line, ch: endCh });
            return true;
          }
          testIndex += 1;
        }
        line += targetString.match(/\n/g)?.length ?? 0;
        if (/\n/.test(targetString)) {
          // 如果有换行，则开始位置的字符计数从最后一个换行开始计数
          beginCh = targetString.replace(/^[\w\W]*\n([^\n]*)$/, '$1').length;
        } else {
          // 如果没有换行，则继续按上次的beginCh为起始开始计数
          beginCh += targetString.length;
        }
      }
    }
    return false;
  }

  /**
   * 修改图片尺寸时的回调
   * @param {HTMLElement} htmlElement 被拖拽的图片标签
   * @param {Object} style 图片的属性（宽高）
   */
  changeImgSize(htmlElement, style) {
    this.imgSize = `#${Math.round(style.width)}px #${Math.round(style.height)}px`;
    this.changeImgValue();
  }

  /**
   * 修改图片样式时的回调
   * @param {HTMLElement} htmlElement 被修改演示的图片标签
   * @param {Object} type 图片的属性（边框、阴影、圆角、对齐方式）
   */
  changeImgStyle(htmlElement, type) {
    switch (type) {
      case 'border':
      case 'shadow':
      case 'radius':
        this.changeImgDecorationStyle(htmlElement, type);
        break;
      case 'left':
      case 'right':
      case 'center':
      case 'float-left':
      case 'float-right':
      case 'clear-align':
        this.changeImgAlignmentStyle(htmlElement, type);
        break;
    }
  }

  /**
   * 修改图片装饰样式
   * @param {HTMLElement} htmlElement 被修改演示的图片标签
   * @param {Object} type 图片的属性（边框、阴影、圆角）
   */
  changeImgDecorationStyle(htmlElement, type) {
    const typeProp = {
      border: { reg: /#(border|B) */g, v: 'B' },
      shadow: { reg: /#(shadow|S) */g, v: 'S' },
      radius: { reg: /#(radius|R) */g, v: 'R' },
    };
    const typeReg = typeProp[type].reg;
    if (typeReg.test(this.imgDeco)) {
      this.imgDeco = this.imgDeco.replaceAll(typeReg, '');
    } else {
      this.imgDeco += `#${typeProp[type].v}`;
    }
    this.changeImgValue();
  }

  /**
   * 修改图片装饰样式
   * @param {HTMLElement} htmlElement 被修改演示的图片标签
   * @param {Object} type 图片的属性（左对齐、居中、右对齐、左浮动、右浮动）
   */
  changeImgAlignmentStyle(htmlElement, type) {
    this.imgAlign = type === 'clear-align' ? '' : `#${type}`;
    this.changeImgValue();
  }

  changeImgValue() {
    this.editor.editor.replaceSelection(
      [this.imgSize, this.imgDeco, this.imgAlign].filter((v) => v).join(' '),
      'around',
    );
  }

  /**
   * 预览区域编辑器的容器
   * @param {string} trigger 触发方式
   * @param {string} type 容器类型（用作样式名：cherry-previewer-{type}）
   */
  $createPreviewerBubbles(trigger = 'click', type = 'img-size-handler') {
    if (!this.bubble[trigger]) {
      this.bubble[trigger] = document.createElement('div');
      this.bubble[trigger].className = `cherry-previewer-${type}`;
      this.previewerDom.after(this.bubble[trigger]);

      if (trigger === 'hover') {
        this.bubble[trigger].addEventListener('mouseover', this.removeHoverBubble.cancel);
        this.bubble[trigger].addEventListener('mouseout', this.removeHoverBubble);
      }
      // 暂时让最上层容器的overflow变成hidden
      this.previewer.$cherry.wrapperDom.style.overflow = 'hidden';
    }
  }

  $showBorderBubbles() {}

  $showBtnBubbles() {}
}
